import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { MapPin, Phone, Clock, Navigation, Coins, ArrowRight, Sparkles } from "lucide-react";
import { calcExchange, EXCHANGE_DEFAULTS, EXCHANGE_PURITY_ORNAMENT, exchangePurityFor } from "../../lib/constants";
import { useAuth } from "../../lib/auth";

// Shown only if the backend has no stores yet (keeps the page from ever
// looking broken) — the moment an admin adds stores in CRM → Stores, these
// are replaced entirely by the live data.
const FALLBACK_STORES = [
  {
    id: "store-belagavi",
    name: "Swayambhu Gold",
    address: "Samadevi Galli, Belagavi, Karnataka 590001",
    phone: "+919980187987",
    hours: "10:00 AM – 9:00 PM",
    image_url: "",
    map_embed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3837.920615801485!2d74.50742247495037!3d15.860769084788913!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf6787c9a465cd%3A0x2fa264648545a914!2sSwayambhu%20Gold%20Private%20Limited!5e0!3m2!1sen!2sin!4v1782823912286!5m2!1sen!2sin",
    directions_url: "https://www.google.com/maps/dir/?api=1&destination=15.860769084788913,74.50742247495037",
  },
  {
    id: "store-hubli",
    name: "Shri Mahaganapati Jewellers",
    address: "Station Road, Khanapur, Karnataka 580020",
    phone: "+919980187987",
    hours: "10:00 AM – 9:00 PM",
    image_url: "",
    map_embed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3842.1052578681038!2d74.50327809678956!3d15.639382399999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbf6b69a34fe1cd%3A0xaea473e0615a99bb!2sM.%20G.%20Jewellers!5e0!3m2!1sen!2sin!4v1782825140006!5m2!1sen!2sin",
    directions_url: "https://www.google.com/maps/dir/?api=1&destination=M.+G.+Jewellers+Khanapur",
  },
];

export function ShowroomsGrid() {
  const [stores, setStores] = useState(FALLBACK_STORES);
  useEffect(() => {
    api.get("/api/stores").then((r) => { if ((r.data || []).length > 0) setStores(r.data); }).catch(() => {});
  }, []);
  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      {stores.map((s) => {
        const directions = s.directions_url || `https://maps.google.com/?q=${encodeURIComponent(s.address || s.name)}`;
        return (
          <div key={s.id || s.name} data-testid={`store-${s.id || s.name}`}>
            <div className="group relative aspect-[4/5] sm:aspect-[4/3] overflow-hidden rounded-xl border border-line bg-pearl">
              {s.image_url ? (
                <img src={s.image_url} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-ash text-sm">No photo yet</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-maroon-dark/90 via-maroon-dark/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
              <div className="absolute inset-0 flex flex-col justify-end p-5 text-pearl opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                <h3 className="font-display text-2xl">{s.name}</h3>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-start gap-2"><MapPin size={14} className="text-gold-light mt-0.5 shrink-0"/> {s.address}</div>
                  {s.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-gold-light shrink-0"/> {s.phone}</div>}
                  {s.hours && <div className="flex items-center gap-2"><Clock size={14} className="text-gold-light shrink-0"/> {s.hours}</div>}
                </div>
                {s.map_embed && (
                  <div className="mt-3 rounded overflow-hidden">
                    <iframe src={s.map_embed} title={s.name} className="w-full h-36 border-0" loading="lazy" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen/>
                  </div>
                )}
              </div>
              <div className="absolute bottom-3 left-4 text-pearl font-display text-xl drop-shadow group-hover:opacity-0 transition-opacity duration-200 pointer-events-none">
                {s.name}
              </div>
            </div>
            <a href={directions} target="_blank" rel="noreferrer" className="btn-maroon mt-4 w-full justify-center" data-testid={`store-navigate-${s.id || s.name}`}>
              <Navigation size={14}/> Navigate to {s.name}
            </a>
          </div>
        );
      })}
    </div>
  );
}

export function Stores() {
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12">
        <div className="text-center"><div className="eyebrow">Visit Us</div><h1 className="font-display text-5xl text-maroon mt-1">Our Showrooms</h1></div>
        <div className="royal-divider my-10"/>
        <ShowroomsGrid />
      </div>
    </StorefrontLayout>
  );
}

export function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/api/site-content/our-story").then((r) => setContent(r.data || null)).catch(() => {});
  }, []);

  const eyebrow = content?.eyebrow ?? "Our Story";
  const title = content?.title ?? "A House Built on Trust";
  const blocks = content?.blocks || [];

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12 max-w-4xl mx-auto" data-testid="our-story-page">
        <div className="text-center">
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <h1 className="font-display text-6xl text-maroon mt-2">{title}</h1>
        </div>
        <div className="royal-divider my-10"/>

        {content?.hero_image_url && (
          <img src={content.hero_image_url} alt={title} className="w-full mb-10 aspect-[16/9] object-cover" data-testid="our-story-hero"/>
        )}

        {blocks.map((b, i) => {
          if (b.type === "heading") {
            return <h2 key={i} className="font-display text-3xl text-maroon mt-10">{b.text}</h2>;
          }
          if (b.type === "image") {
            return (
              <figure key={i} className="mt-12">
                <img src={b.image_url} alt={b.caption || ""} className="w-full aspect-[16/9] object-cover"/>
                {b.caption && <figcaption className="text-xs text-ash mt-2 text-center">{b.caption}</figcaption>}
              </figure>
            );
          }
          return (
            <p key={i} className={`text-lg text-ink leading-relaxed ${i === 0 ? "" : "mt-6"}`}>{b.text}</p>
          );
        })}

        {!content && (
          <p className="text-lg text-ink leading-relaxed">Loading…</p>
        )}
      </div>
    </StorefrontLayout>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/api/contact", form);
    setDone(true);
  };
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12 max-w-2xl mx-auto">
        <div className="text-center"><div className="eyebrow">Reach us</div><h1 className="font-display text-5xl text-maroon mt-1">Get in touch</h1></div>
        <div className="royal-divider my-8"/>
        {done ? (
          <div className="text-center py-10 text-maroon">Thank you. We will reach out shortly.</div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-line p-6 space-y-3">
            <input placeholder="Name" required className="w-full border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="contact-name"/>
            <input placeholder="Email" type="email" required className="w-full border border-line px-3 py-2 bg-pearl" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="contact-email"/>
            <input placeholder="Phone" className="w-full border border-line px-3 py-2 bg-pearl" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="contact-phone"/>
            <textarea rows={5} placeholder="How may we help?" required className="w-full border border-line px-3 py-2 bg-pearl" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="contact-message"/>
            <button className="btn-maroon w-full justify-center" data-testid="contact-submit">Send</button>
          </form>
        )}
      </div>
    </StorefrontLayout>
  );
}

export function GoldPrograms() {
  const SCHEMES = [
    { name: "Golden Harvest 11", desc: "Pay 11 monthly instalments, we pay the 12th. Redeem in any design at maturity rate.", min: 2000, months: 11, bonusMonths: 1, metal: "Gold" },
    { name: "Silver Sprout 6", desc: "Half-yearly silver accumulation. Perfect for festival gifting.", min: 2000, months: 6, bonusMonths: 1, metal: "Silver" },
    { name: "Bridal Bloom 24", desc: "Two-year wedding savings plan with double bonus month and personal stylist.", min: 10000, months: 24, bonusMonths: 2, metal: "Gold" },
    { name: "Daughter's Dream", desc: "Long-term plan crafted with parents in mind. Lock today's rate, redeem at college, marriage or first job.", min: 5000, months: 36, bonusMonths: 1, metal: "Gold" },
  ];

  const [selected, setSelected] = useState(SCHEMES[0]);
  const [monthly, setMonthly] = useState(SCHEMES[0].min);

  const pickScheme = (s) => { setSelected(s); setMonthly(s.min); };

  const paid = monthly * selected.months;                       // what customer pays in
  const bonus = monthly * selected.bonusMonths;                 // shop's contribution
  const maturity = paid + bonus;                                // total redeemable value
  const inr = (n) => `₹${Intl.NumberFormat("en-IN").format(Math.round(n))}`;

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12 max-w-6xl mx-auto">
        <div className="text-center">
          <div className="eyebrow">Save · Grow · Gift</div>
          <h1 className="font-display text-5xl text-maroon mt-1">Gold Programs</h1>
          <p className="text-ash mt-3 max-w-xl mx-auto">Save a little every month; we add a bonus month at the end. Redeem in gold of any design at the maturity-day rate.</p>
        </div>
        <div className="royal-divider my-10"/>

        {/* Featured: Swayambhu Gold Scheme */}
        <div className="relative overflow-hidden rounded-2xl border-2 border-gold bg-gradient-to-r from-maroon to-maroon-dark text-pearl px-5 sm:px-7 lg:px-12 py-9 mb-12 reveal" data-testid="featured-gold-scheme">
          <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-gold text-ink text-[9px] sm:text-[10px] font-semibold uppercase tracking-widest px-2 sm:px-2.5 py-1 rounded-full"><Sparkles size={12}/> Most Popular</span>
          <div className="grid lg:grid-cols-2 gap-8 items-center mt-6 lg:mt-0">
            <div>
              <div className="inline-flex items-center gap-2 eyebrow text-gold-light"><Coins size={15}/> Swayambhu Gold Scheme</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2">Invest monthly, lock today's gold price</h2>
              <p className="text-pearl/80 mt-3 text-sm max-w-md">
                Invest any amount you like each month for 12 months (minimum ₹5,000). Whatever you pay is converted to
                gold at that day's rate and credited to you. At the end, take your accumulated gold — making charges waived.
              </p>
              <Link to="/gold-scheme" className="btn-gold mt-6 inline-flex" data-testid="cta-gold-scheme-programs">
                Explore &amp; Join <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
              {[["12", "monthly instalments"], ["Flexible", "amount you choose"], ["Free", "making at the end"]].map(([a, b], i) => (
                <div key={i} className="bg-pearl/10 border border-pearl/20 rounded-xl py-4 sm:py-5 px-1 sm:px-2">
                  <div className="font-display text-base sm:text-2xl text-gold-light leading-tight break-words">{a}</div>
                  <div className="text-[9px] sm:text-[10px] text-pearl/70 uppercase tracking-wider sm:tracking-widest mt-1 leading-tight">{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calculator */}
        <div className="grid lg:grid-cols-5 gap-6 items-stretch mb-14">
          {/* Left: scheme picker + input */}
          <div className="lg:col-span-3 bg-white border border-line p-6">
            <div className="eyebrow text-[10px] mb-3">1 · Choose your plan</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SCHEMES.map((s) => {
                const on = selected.name === s.name;
                return (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => pickScheme(s)}
                    data-testid={`scheme-pick-${s.name}`}
                    className={`text-left p-4 rounded-xl border transition-all ${on ? "border-maroon bg-maroon text-pearl shadow-md" : "border-line hover:border-maroon"}`}
                  >
                    <div className={`text-[9px] uppercase tracking-widest ${on ? "text-gold-light" : "text-ash"}`}>{s.metal} · {s.months} months</div>
                    <div className={`font-display text-lg mt-0.5 ${on ? "text-pearl" : "text-maroon"}`}>{s.name}</div>
                    <div className={`text-[11px] mt-1 ${on ? "text-pearl/70" : "text-ash"}`}>from ₹{Intl.NumberFormat("en-IN").format(s.min)}/mo</div>
                  </button>
                );
              })}
            </div>

            <div className="eyebrow text-[10px] mt-6 mb-2">2 · Monthly instalment</div>
            <div className="flex items-center gap-3">
              <span className="font-display text-2xl text-maroon">₹</span>
              <input
                type="number"
                min={selected.min}
                step={500}
                value={monthly}
                onChange={(e) => setMonthly(+e.target.value || 0)}
                className="flex-1 border border-line rounded-lg px-4 py-3 bg-pearl font-display text-2xl text-maroon focus:border-maroon outline-none"
                data-testid="calc-monthly"
              />
            </div>
            {/* quick chips */}
            <div className="flex flex-wrap gap-2 mt-3">
              {[selected.min, selected.min * 2, selected.min * 5].map((v) => (
                <button key={v} type="button" onClick={() => setMonthly(v)}
                  className={`text-xs rounded-full px-3 py-1 border transition-colors ${monthly === v ? "border-maroon bg-maroon text-pearl" : "border-line hover:border-maroon"}`}>
                  ₹{Intl.NumberFormat("en-IN").format(v)}
                </button>
              ))}
            </div>
            {monthly < selected.min && (
              <div className="text-[11px] text-red-600 mt-2">Minimum for {selected.name} is ₹{Intl.NumberFormat("en-IN").format(selected.min)}/month.</div>
            )}
            <p className="text-[11px] text-ash mt-4 leading-relaxed">{selected.desc}</p>
          </div>

          {/* Right: result card */}
          <div className="lg:col-span-2 bg-gradient-to-br from-maroon to-maroon-dark text-pearl p-8 rounded-xl flex flex-col justify-center royal-frame">
            <div className="eyebrow text-gold-light">You'll redeem</div>
            <div className="font-display text-5xl mt-2 leading-none">{inr(maturity)}</div>
            <div className="text-[11px] text-pearl/60 mt-2">worth of {selected.metal.toLowerCase()} at maturity-day rate</div>

            <div className="mt-6 space-y-3 text-sm border-t border-pearl/20 pt-5">
              <div className="flex justify-between"><span className="text-pearl/70">You pay ({selected.months} months)</span><span className="font-medium">{inr(paid)}</span></div>
              <div className="flex justify-between"><span className="text-pearl/70">We add ({selected.bonusMonths} month{selected.bonusMonths > 1 ? "s" : ""})</span><span className="font-medium text-gold-light">+ {inr(bonus)}</span></div>
              <div className="flex justify-between border-t border-pearl/20 pt-3"><span>Total value</span><span className="font-display text-xl">{inr(maturity)}</span></div>
            </div>

            <a
              href={`https://wa.me/919980187987?text=${encodeURIComponent(`Hi, I'd like to enrol in ${selected.name} at ₹${monthly}/month. Total value ${inr(maturity)}.`)}`}
              target="_blank"
              rel="noreferrer"
              className="btn-gold mt-7 w-full justify-center"
              data-testid="calc-enrol"
            >
              Enrol via WhatsApp →
            </a>
            <div className="text-[9px] text-pearl/40 mt-3 text-center">Indicative. Final gold weight depends on the rate on your redemption day.</div>
          </div>
        </div>

        {/* All plans reference grid */}
        <div className="text-center mb-8">
          <div className="eyebrow">All Plans</div>
          <h2 className="font-display text-3xl text-maroon mt-1">Every way to save with us</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SCHEMES.map((p) => (
            <div key={p.name} className="bg-white border border-line rounded-xl p-6 hover-lift flex flex-col" data-testid={`scheme-${p.name}`}>
              <div className="eyebrow text-[9px]">{p.metal} · {p.months} + {p.bonusMonths} months</div>
              <h3 className="font-display text-xl text-maroon mt-1">{p.name}</h3>
              <div className="text-[11px] text-ash mt-0.5">from ₹{Intl.NumberFormat("en-IN").format(p.min)}/month</div>
              <p className="text-sm text-ash mt-2 flex-1">{p.desc}</p>
              <button
                onClick={() => { pickScheme(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="btn-outline mt-4 inline-flex justify-center"
                data-testid={`scheme-calc-${p.name}`}
              >
                Calculate savings →
              </button>
            </div>
          ))}
        </div>
      </div>
    </StorefrontLayout>
  );
}

export function Exchange() {
  const { user } = useAuth();
  const [w, setW] = useState(20);
  const [purity, setPurity] = useState(22);
  // Raw gold (bar/coin) assays at 100%; an ornament never does.
  const [isRaw, setIsRaw] = useState(false);
  const [rate24k, setRate24k] = useState(0);
  const [cfg, setCfg] = useState(EXCHANGE_DEFAULTS);

  useEffect(() => {
    api.get("/api/rates/current").then((r) => setRate24k(r.data.gold_24k || 0)).catch(() => {});
    api.get("/api/site-settings").then((r) => setCfg({
      gstPercent: r.data?.exchange_gst_percent ?? EXCHANGE_DEFAULTS.gstPercent,
      deductionPer10g: r.data?.exchange_deduction_per_10g ?? EXCHANGE_DEFAULTS.deductionPer10g,
    })).catch(() => {});
  }, []);

  // Customers choose the form and carat; the buy-back purity is looked up.
  const purityPercent = exchangePurityFor(purity, isRaw);
  const calc = calcExchange(w, purityPercent, rate24k, cfg);

  // Pre-written WhatsApp message with the customer's own details auto-filled.
  const bookVisitLink = () => {
    const est = `₹${Intl.NumberFormat("en-IN").format(Math.round(calc.value))}`;
    const form = isRaw ? "raw gold (bar/coin)" : "an ornament";
    const pur = isRaw ? "approx 24K" : `${purity}K`;
    const lines = [
      "Hi Swayambhu Gold,",
      user?.name ? `This is ${user.name}${user?.phone ? ` (${user.phone})` : ""}.` : "I'd like to exchange my old gold.",
      `I want to exchange ${w} grams of old gold — it is ${form}, purity ${pur}.`,
      `The website's estimated value is ${est}.`,
      "I'd like to book a visit to the showroom to exchange this and buy new gold. Please let me know a suitable time I can come in.",
    ];
    return `https://wa.me/919980187987?text=${encodeURIComponent(lines.join("\n"))}`;
  };
  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-12 max-w-3xl mx-auto">
        <div className="text-center"><div className="eyebrow">Old Gold</div><h1 className="font-display text-5xl text-maroon mt-1">Gold Exchange</h1></div>
        <div className="royal-divider my-8"/>
        <p className="text-center text-ash">Bring your old gold to any showroom. Get today's value, instantly — with full transparency.</p>
        <div className="bg-white border border-line p-6 mt-8 grid md:grid-cols-2 gap-6">
          <div>
            <div className="eyebrow mb-2">Old gold weight (g)</div>
            <input type="number" value={w} onChange={(e) => setW(+e.target.value || 0)} className="w-full border border-line px-3 py-2 bg-pearl" data-testid="exchange-weight"/>
            <div className="eyebrow mt-4 mb-2">What are you exchanging?</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setIsRaw(false)}
                className={`border px-3 py-2 text-sm ${!isRaw ? "border-maroon text-maroon font-medium" : "border-line text-ash"}`}
                data-testid="exchange-form-ornament">
                Ornament
              </button>
              <button type="button" onClick={() => setIsRaw(true)}
                className={`border px-3 py-2 text-sm ${isRaw ? "border-maroon text-maroon font-medium" : "border-line text-ash"}`}
                data-testid="exchange-form-raw">
                Raw gold (bar / coin)
              </button>
            </div>

            {isRaw ? null : (
              <>
                <div className="eyebrow mt-4 mb-2">Purity (Karat)</div>
                <select value={purity} onChange={(e) => setPurity(+e.target.value)} className="w-full border border-line px-3 py-2 bg-pearl" data-testid="exchange-purity">
                  {Object.keys(EXCHANGE_PURITY_ORNAMENT).map((k) => (
                    <option key={k} value={k}>{k}K</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="bg-maroon text-pearl p-8 text-center" data-testid="exchange-result">
            <div className="eyebrow text-gold-light">Estimated value</div>
            <div className="font-display text-5xl mt-2">₹{Intl.NumberFormat("en-IN").format(Math.round(calc.value))}</div>
            <p className="text-[11px] mt-4 text-pearl/70 max-w-xs mx-auto">
              This is an approximate value. The actual price may vary based on the
              tested purity of your ornament at the counter.
            </p>

            <a href={bookVisitLink()} target="_blank" rel="noreferrer" className="btn-gold mt-6 inline-flex">Book a Visit</a>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
// Small label/value row used in the exchange breakdown
const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between ${strong ? "font-semibold text-gold-light" : ""}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
